import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircleDollarSign, HandCoins, HeartPulse, ListChecks, Users } from "lucide-react";
import Tabs from "./ui/Tabs";

/**
 * Barra de abas do dashboard, compartilhada entre as telas que a desenham.
 *
 * Cada aba é um destino, não um pedaço de estado: Vendas, Tarefas e Equipe vivem em
 * `/dashboard?aba=...` e Comissões na rota própria dela, que é um relatório de 800 linhas com
 * filtros próprios. Ficar em rotas separadas é o que permite cada aba carregar só o que ela usa —
 * juntar tudo numa página só faria toda troca de aba pagar pelo custo das outras.
 *
 * Relatórios novos NÃO entram aqui automaticamente: onde cada um se encaixa é decidido quando
 * for criado.
 */
export const DASHBOARD_TABS = [
  { value: "vendas", label: "Vendas", icon: CircleDollarSign, to: "/dashboard?aba=vendas" },
  { value: "tarefas", label: "Tarefas", icon: ListChecks, to: "/dashboard?aba=tarefas" },
  { value: "equipe", label: "Equipe", icon: Users, to: "/dashboard?aba=equipe" },
  { value: "comissoes", label: "Repasse", icon: HandCoins, to: "/relatorios/comissao-repasse" },
  { value: "churn", label: "Churn", icon: HeartPulse, to: "/relatorios/churn" },
];

export const DASHBOARD_TABS_ID = "dashboard";

/** Abas que o dashboard resolve trocando `?aba=`, sem sair da página. */
export const DASHBOARD_PAGE_TABS = DASHBOARD_TABS.filter((tab) => tab.to.startsWith("/dashboard"));

/** Blocos que cada aba pede ao `/api/dashboard/gestao-comercial`. Equipe não usa esse endpoint. */
export const DASHBOARD_TAB_SECOES = {
  vendas: ["PRODUCAO"],
  tarefas: ["TAREFAS"],
  equipe: [],
};

/**
 * Aba ativa a partir da URL, ou null quando a rota não é do dashboard. Fica aqui, e não no menu,
 * porque quem sabe que `/dashboard` sem `?aba=` abre Vendas é este arquivo.
 */
export function resolveDashboardTab({ pathname = "", search = "" } = {}) {
  const routeTab = DASHBOARD_TABS.find(
    (tab) => !tab.to.startsWith("/dashboard") && (pathname === tab.to || pathname.startsWith(`${tab.to}/`))
  );
  if (routeTab) return routeTab.value;
  if (pathname === "/dashboard" || pathname.startsWith("/dashboard/")) {
    const aba = new URLSearchParams(search).get("aba");
    return DASHBOARD_PAGE_TABS.some((tab) => tab.value === aba) ? aba : "vendas";
  }
  return null;
}

export default function DashboardTabs({ value, className = "self-start" }) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const handleChange = (next) => {
    if (next === value) return;
    const target = DASHBOARD_TABS.find((tab) => tab.value === next);
    if (!target) return;

    const isPageTab = DASHBOARD_PAGE_TABS.some((tab) => tab.value === next);
    const stayingOnDashboard = isPageTab && DASHBOARD_PAGE_TABS.some((tab) => tab.value === value);

    // Entre abas da própria página, só troca o parâmetro (replace: o histórico não vira uma pilha
    // de abas). Sair para outra rota é navegação de verdade, com volta pelo botão do navegador.
    if (stayingOnDashboard) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("aba", next);
          return params;
        },
        { replace: true }
      );
      return;
    }

    navigate(target.to);
  };

  return (
    <Tabs
      items={DASHBOARD_TABS}
      value={value}
      onChange={handleChange}
      idPrefix={DASHBOARD_TABS_ID}
      ariaLabel="Seções do dashboard"
      className={className}
    />
  );
}
